import { getCachedGpPubContainers, getCachedPubContainers, getMapState, getOmjsLayerConfig } from 'selectors';
import { actions as mapActions } from 'services/map/map-service';
import { ILayersConf } from 'types';
import { routes } from 'routes';
import i18n from 'i18next';
import { isMobile } from 'react-device-detect';

export class UTFGrid {
    view: omjs.View;

    layer: omjs.ViewUTFGridLayer;
    mapCode: string;
    gridFields: Set<string> = new Set();
    rasterLayers: number[];

    url: string;

    gpLayer: omjs.ViewUTFGridLayer;
    gpMapCode: string;
    gpGridFields: Set<string> = new Set();
    gpRasterLayers: number[];
    gpReady: boolean = false;

    gpKey = '';

    showTooltip: (coordinate: number[], text: string) => void;
    hideTooltip: () => void;
    setTooltipPos: (coordinate: number[]) => void;

    constructor(
        view: omjs.View,
        rasterLayers: number[],
        showTooltip,
        hideTooltip,
        setTooltipPos
    ) {
        this.view = view;
        this.url = view.options['tilesUrl'].replace('{a-c}-', '');
        this.rasterLayers = rasterLayers;
        this.showTooltip = showTooltip;
        this.hideTooltip = hideTooltip;
        this.setTooltipPos = setTooltipPos;

        this.mapCode = 'iris_new';
        this.gpMapCode = 'gen_plans';

        this.layer = new omjs.View.UTFGridLayer({
            attributes: {
                grid: 1,
                map_code: '',
                layers: ''
            },
            url: this.url
        });

        this.gpLayer = new omjs.View.UTFGridLayer({
            attributes: {
                grid: 1,
                map_code: '',
                layers: ''
            },
            url: this.url
        });

        const pubContainers = getCachedPubContainers(false);
        pubContainers.forEach((cont) => {
            if ((cont.get('type') === 'layer' || cont.get('type') === 'virtual') && cont.get('render_type') === 0) {
                const config = getOmjsLayerConfig(false, cont.get('id')) as ILayersConf;
                const { objectNameColumnCode, objectNameAutoChoose } = config;
                if (!objectNameAutoChoose && objectNameColumnCode) {
                    const columnCode = [ 'kod', 'tooltip' ].includes(objectNameColumnCode)
                        ? objectNameColumnCode
                        : `${objectNameColumnCode}_${i18n.language}`;
                    this.gridFields.add(columnCode);
                }
            }
        });

        this.setUrl();
        // слои utfgrid отправляют запросы в том порядке, в котором добавлялись в карту, первым добавляем слой генпланов
        view.addLayer(this.gpLayer);
        view.addLayer(this.layer);

        this.layer.on('click', (evt) => {
            const key = `${evt.coordinate.toString()}:${evt.zoom}`;
            if (key === this.gpKey) return;

            mapActions.hideBalloon();
            const { cadastreVisible } = getMapState(false);
            let pkk_settings = this.view.options.publication.settings.customWidgets.pkk_settings?.values;
            let isDrawing = view.getPublicMapDraw().isDrawing();
            if (cadastreVisible && pkk_settings?.pkk_show_balloon && !isDrawing) {
                mapActions.showLoader(evt.coordinate);
                mapActions.showPkkBalloon(evt.coordinate);
                return;
            }

            if (!evt.data || !this.layer['enabled']) {
                this.hideTooltip();
                if (isMobile) {
                    mapActions.getConnections([ 0, 0 ]);
                }
                return;
            }
            evt.gridLoader.then((e) => {
                const layerCode = e.data['layer'];
                const objectId = e.data['orbis_id'];
                const layer_settings = getOmjsLayerConfig(false, e.data.layer) as ILayersConf;
                if (layer_settings.balloonEnabled || layer_settings.objectWindowEnabled) {
                    mapActions.showLoader(evt.coordinate);
                    routes.redirect(routes.object, {
                        layerCode,
                        objectId
                    });
                }
            });
        });

        this.layer
            .on('mouseover', (e) => {
                const key = `${e.coordinate.toString()}:${e.zoom}`;
                if (key === this.gpKey) return;
                if (!e.data || !this.layer['enabled']) {
                    this.hideTooltip();
                    return;
                }

                const layer_settings = getOmjsLayerConfig(false, e.data.layer) as ILayersConf;
                if (layer_settings.balloonEnabled || layer_settings.objectWindowEnabled) {
                    this.view.getContainer().css('cursor', 'pointer');
                }

                if (!layer_settings.objectNameAutoChoose && layer_settings.objectNameColumnCode) {
                    const tooltipText = e.data[`${layer_settings.objectNameColumnCode}_${i18n.language}`]
                        ? e.data[`${layer_settings.objectNameColumnCode}_${i18n.language}`]
                        : e.data[layer_settings.objectNameColumnCode];
                    this.showTooltip(e.coordinate, tooltipText);
                }
            })
            .on('mousemove', (e) => {
                const key = `${e.coordinate.toString()}:${e.zoom}`;
                if (key === this.gpKey) return;
                if (!e.data || !this.layer['enabled']) {
                    this.hideTooltip();
                } else {
                    this.setTooltipPos(e.coordinate);
                }
            })
            .on('mouseout', (e) => {
                this.hideTooltip();
                this.view.getContainer().css('cursor', 'auto');
            });
    }

    prepareGpLayer(gpRasterLayers: number[]) {
        if (this.gpReady) return;
        this.gpRasterLayers = gpRasterLayers;
        const pubContainers = getCachedGpPubContainers(false);
        pubContainers.forEach((cont) => {
            if ((cont.get('type') === 'layer' || cont.get('type') === 'virtual') && cont.get('render_type') === 0) {
                const layer_settings = getOmjsLayerConfig(false, cont.get('id')) as ILayersConf;
                if (!layer_settings?.objectNameAutoChoose || layer_settings?.objectNameColumnCode) {
                    this.gpGridFields.add(layer_settings?.objectNameColumnCode);

                    if (![ 'kod', 'tooltip', 'orbis_id' ].includes(layer_settings?.objectNameColumnCode)) {
                        const columnCode = `${layer_settings?.objectNameColumnCode}_${i18n.language}`;
                        this.gpGridFields.add(columnCode);
                    }
                }
            }
        });

        this.gpLayer.on('click', (evt) => {
            if (!evt.data || !this.gpLayer['enabled']) {
                this.hideTooltip();
                if (isMobile) {
                    mapActions.getConnections([ 0, 0 ]);
                }
                return;
            }

            evt.gridLoader.then((e) => {
                const layerCode = e.data['layer'];
                const objectId = e.data['orbis_id'];
                const layer_settings = getOmjsLayerConfig(false, e.data.layer) as ILayersConf;

                if (layer_settings?.balloonEnabled || layer_settings?.objectWindowEnabled) {
                    this.gpKey = `${evt.coordinate.toString()}:${evt.zoom}`;
                    mapActions.showLoader(evt.coordinate);
                    routes.redirect(routes.object, {
                        layerCode,
                        objectId
                    });
                }
            });
        });

        this.gpLayer
            .on('mouseover', (e) => {
                if (!e.data || !this.gpLayer['enabled']) {
                    this.hideTooltip();
                    return;
                }

                const layer_settings = getOmjsLayerConfig(false, e.data.layer) as ILayersConf;
                if (layer_settings?.balloonEnabled || layer_settings?.objectWindowEnabled) {
                    this.gpKey = `${e.coordinate.toString()}:${e.zoom}`;
                    this.view.getContainer().css('cursor', 'pointer');
                }

                if (!layer_settings.objectNameAutoChoose && layer_settings.objectNameColumnCode) {
                    const tooltipText = e.data[`${layer_settings.objectNameColumnCode}_${i18n.language}`]
                        ? e.data[`${layer_settings.objectNameColumnCode}_${i18n.language}`]
                        : e.data[layer_settings.objectNameColumnCode];
                    this.showTooltip(e.coordinate, tooltipText);
                }
            })
            .on('mousemove', (e) => {
                if (!e.data || !this.gpLayer['enabled']) {
                    this.hideTooltip();
                } else {
                    this.gpKey = `${e.coordinate.toString()}:${e.zoom}`;
                    this.setTooltipPos(e.coordinate);
                }
            })
            .on('mouseout', (e) => {
                this.hideTooltip();
                this.view.getContainer().css('cursor', 'auto');
            });
        this.setGpUrl();
        this.gpReady = true;
    }

    setLayers(layers) {
        this.rasterLayers = layers;
        this.setUrl();
    }

    setUrl() {
        const gridLayers = [ ...this.rasterLayers ];
        this.layer.toggleTiles((Boolean(gridLayers.length)));
        this.layer.set('map_code', this.mapCode);
        this.layer.set('layers', gridLayers);
        this.layer.set('grid_fields', Array.from(this.gridFields));
    }

    setGpLayers(layers) {
        if (!this.gpReady) this.prepareGpLayer(layers);
        this.gpRasterLayers = layers;
        this.setGpUrl();
    }

    setGpUrl() {
        const gridLayers = [ ...this.gpRasterLayers ];
        this.gpLayer.toggleTiles((Boolean(gridLayers.length)));
        this.gpLayer.set('map_code', this.gpMapCode);
        this.gpLayer.set('layers', gridLayers);
        this.gpLayer.set('grid_fields', Array.from(this.gpGridFields));
    }
}
